import { GetStaticProps, GetStaticPaths, InferGetStaticPropsType } from "next";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { NextSeo } from "next-seo";
import ReactToPrint from "react-to-print";
import Error from "../../../404";
import Head from "next/head";
import { AiFillPrinter, AiOutlineFullscreen } from "react-icons/ai";
import BeatLoader from "react-spinners/BeatLoader";
import { ScrollArea } from "@mantine/core";
import { FullScreen, useFullScreenHandle } from "react-full-screen";

export const getStaticProps: GetStaticProps = async (context) => {
  const ticker = context.params?.ticker.toString().toUpperCase();
  const { getDoc, doc } = await import("firebase/firestore");
  const { db } = await import("../../../../lib/firebase");
  const [companyData, descriptions] = await Promise.all([
    getDoc(doc(db, "companies_profiles", ticker)),
    import("../../../../public/jsons/other/descriptions.json"),
  ]);

  const company = companyData.data();

  if (!company) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      company,
      descriptions: descriptions.default,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    fallback: "blocking",
    paths: [],
  };
};

export default function Page({
  company,
  setCurrentTicker,
  recentSearches,
  descriptions,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  useEffect(() => {
    if (company) {
      setCurrentTicker([company.ticker, company.name, company.coverage]);
    }
  }, [setCurrentTicker, company]);

  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<string>(null);

  useEffect(() => {
    if (data || loading) return;
    const fetchFiling = async () => {
      setLoading(true);
      const filing = await fetch(
        `/api/sec-filings/filing?cik=${company.cik}&accessionNumber=${router.query.accessionNumber}&doc=${router.query.doc}&type=${router.query.type}`
      );
      const { decompressSync, strToU8, strFromU8 } = await import("fflate");
      setData(strFromU8(decompressSync(strToU8(await filing.text(), true))));
      setLoading(false);
    };
    fetchFiling();
  }, [data, router.query, company, loading]);

  const type = router.query.type || "";
  const ticker = router.query.ticker?.toString().toUpperCase();

  const contentToPrint = useRef(null);

  const favoriteCount = recentSearches?.filter((s) => s.heart).length > 0;
  const handleFullscreen = useFullScreenHandle();

  const detailedType = descriptions.find((t) => t.type === type)?.description;
  const title = `${company.name} (${company.ticker}) ${
    type ? (detailedType ? `${type}, ${detailedType}` : type) : ""
  } SEC Filing - 10kreader`;

  const description = `Find the latest SEC Filings and Earning Call Transcripts for ${company.name} (${company.ticker}) at 10kreader.com.`;

  const isMissingData = data === "";

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
      </Head>
      <NextSeo
        title={title}
        description={description}
        noindex={
          router.query.type &&
          ["10-K", "10-Q", "S-1", "20-F", "40-F"].includes(type as string)
            ? true
            : false
        }
        canonical={
          router.query.type && router.query.doc
            ? `https://10kreader.com/company/${ticker.toLowerCase()}/${
                router.query.accessionNumber
              }?type=${router.query.type}&doc=${router.query.doc}`
            : undefined
        }
      />
      <h1
        className={`flex items-center justify-center py-2 text-center font-title text-xl sm:text-left md:text-2xl lg:text-[30px] ${
          favoriteCount ? "h-[7vh]" : "h-[8vh]"
        }`}
      >
        Filing {type} | {ticker}
      </h1>
      <div
        className={`flex w-full flex-col items-center justify-center ${
          isMissingData && "mt-14"
        }`}
      >
        {!isMissingData ? (
          <>
            <div className="hidden h-[3.5vh] w-full items-center justify-end py-3 lg:flex lg:max-w-[1000px] xl:max-w-[1100px]">
              <AiOutlineFullscreen
                size="25"
                className="mr-2 hidden cursor-pointer lg:block "
                onClick={handleFullscreen.enter}
              />
              <ReactToPrint
                trigger={() => (
                  <button
                    className={`mr-2 flex h-7 w-7 items-center justify-center rounded-full duration-150 ease-in  ${
                      !loading && "cursor-pointer hover:bg-white"
                    }`}
                  >
                    <AiFillPrinter size="22" />
                  </button>
                )}
                content={() => contentToPrint.current}
              />
            </div>
            <div
              className={`flex ${
                favoriteCount ? "h-[77vh] lg:h-[75vh]" : "h-[80vh] lg:h-[79vh]"
              } w-full items-center justify-center rounded-lg bg-white lg:max-w-[1000px] xl:max-w-[1100px]`}
            >
              {loading && (
                <BeatLoader
                  color={"rgb(233, 121, 93)"}
                  loading={loading}
                  size={25}
                  speedMultiplier={0.6}
                />
              )}
              {!loading && (
                <FullScreen
                  handle={handleFullscreen}
                  className={`overflow-hidden bg-white lg:max-w-[1000px] xl:max-w-[1100px] ${
                    handleFullscreen.active &&
                    "px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24"
                  }`}
                >
                  <ScrollArea
                    scrollbarSize={5}
                    scrollHideDelay={500}
                    className={`${
                      favoriteCount && !handleFullscreen.active && "h-[72vh]"
                    } ${
                      !favoriteCount && !handleFullscreen.active && "h-[77vh]"
                    } ${handleFullscreen.active && "h-full"}`}
                  >
                    <div
                      className="highlights px-4 print:bg-white print:px-20 sm:px-8 md:px-12"
                      ref={contentToPrint}
                      dangerouslySetInnerHTML={{ __html: data }}
                    ></div>
                  </ScrollArea>
                </FullScreen>
              )}
            </div>
          </>
        ) : (
          <Error recentSearches={recentSearches} />
        )}
      </div>
    </>
  );
}
